import { Link } from "@/components/ui/link"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { format } from "date-fns"
import Loader from "@/components/layout/loader"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import { Customer, useCustomers, useCustomersActions } from "@/api/customers"
import { toast } from "sonner"
import { ConfirmDialog } from "@/components/ux/confirm.dialog"
import { AlertDialog, AlertDialogTrigger } from "@/components/ui/alert-dialog"

interface CustomersTableProps {
  onEdit: (id: string) => void;
}

export default function CustomersTable({ onEdit }: CustomersTableProps) {
  const { customers, hasMore, isLoading, fetchNextPage, isFetchingNextPage } =
    useCustomers()
  const { remove } = useCustomersActions()

  const onRemove = async (customer: Customer) => {
    await remove(customer.id)
    toast.success("Customer removed")
  }

  if (!customers) {
    return <Loader message="Loading customers..." />
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="rounded-t-md">
          <TableHead className="pl-6">Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Created on</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {customers.map((customer) => (
          <TableRow key={customer.id}>
            <TableCell className="pl-6">
              <div className="font-medium flex items-center gap-4">
                <Link to={`/customers/${customer.id}`} data-testid="view">
                  {customer.firstName} {customer.lastName}
                </Link>
              </div>
            </TableCell>
            <TableCell>{customer.email}</TableCell>
            <TableCell>
              {format(new Date(customer.createdAt), "MMM do, yyyy")}
            </TableCell>
            <TableCell>
              <AlertDialog>
                <ConfirmDialog
                  onConfirm={() => onRemove(customer)}
                  title="Remove customer"
                  description={`Are you sure you want to remove ${customer.firstName} ${customer.lastName}?`}
                />
                <OptionsDropdownMenu>
                  <DropdownMenuItem
                    onClick={() => onEdit(customer.id)}
                    data-testid="edit"
                  >
                    Edit
                  </DropdownMenuItem>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem data-testid="delete">
                      Remove
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                </OptionsDropdownMenu>
              </AlertDialog>
            </TableCell>
          </TableRow>
        ))}
        {customers.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-gray-300">
              No customers found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasMore && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={4}>
              <button
                data-testid="load-more"
                onClick={() => fetchNextPage()}
                className="flex flex-col w-full items-center bg-white p-3 text-center"
                disabled={isLoading || isFetchingNextPage}
              >
                {isLoading || isFetchingNextPage ? "Loading..." : "Load more..."}
              </button>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  )
}
